import React, { useEffect, useRef, useState } from "react";
import "./LoginPage.css";
import { useNavigate } from "react-router-dom";
import { isMyGovSkipEnabled, isEidasEnabled } from "../auth/authConfig";
import { useAuthRouter } from "../auth/AuthRouterProvider";
import { getToken, login } from "../auth/AuthProvider";
import { getAuthStateFromSession, logout, storeLoginMethod } from "../session/utils/userSessionStorage";
import useSessionTimer from "../session/hooks/useSessionTimer";
import "../../shared/components/GovHeader/gov-header.css";
import { logger } from "../../shared/utils/logger.client";
import { useTranslation } from "react-i18next";

type Provider = "MYGOV" | "EIDAS" | "MANUAL";
type RedirectState = { provider: Provider; nonce: number } | null;

const OIDC_REDIRECT_KEY = "oidcRedirect";
const OIDC_REDIRECT_AT_KEY = "oidcRedirectAt";

const overlayStyles: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};
const modalStyles: React.CSSProperties = {
  background: "#fff",
  padding: "24px 28px",
  borderRadius: 12,
  minWidth: 320,
  textAlign: "center",
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
};
const spinnerStyles: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "4px solid #eee",
  borderTop: "4px solid #1976d2",
  animation: "spin 0.9s linear infinite",
  margin: "0 auto",
};

const isOidcProvider = (p: any): p is Exclude<Provider, "MANUAL"> => p === "MYGOV" || p === "EIDAS";

function RedirectModal({ provider }: Readonly<{ provider: Exclude<Provider, "MANUAL"> }>) {
  const { t } = useTranslation();

  const label =
    provider === "MYGOV"
      ? t("login.provider.mygov")
      : t("login.provider.eidas");

  return (
    <div style={overlayStyles} role="dialog" aria-modal="true" aria-live="assertive">
      <div style={modalStyles}>
        <div style={spinnerStyles} aria-hidden />
        <h2 style={{ marginTop: 12, fontSize: 18, fontWeight: 700 }}>
          {t("login.redirect.title", { provider: label })}
        </h2>
        <p style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
          {t("login.redirect.message")}
        </p>
      </div>
    </div>
  );
}

const LoginRequester: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthMethod, currentMethod } = useAuthRouter();
  const [showSkip, setShowSkip] = useState<boolean>(false);
  const [showEidas, setShowEidas] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const search = typeof window !== "undefined" ? window.location.search : "";
  const hasOidcParams = /\b(code|state|error)=/.test(search);
  const { t } = useTranslation();
  const { startTimer } = useSessionTimer();

  const initialRedirecting =
      typeof window !== "undefined" && sessionStorage.getItem(OIDC_REDIRECT_KEY) === "1";
  const storedProvider = (localStorage.getItem("loggedMethod") as Provider) || "MYGOV";
  const shouldRestore = initialRedirecting && isOidcProvider(storedProvider) && hasOidcParams;

  const [redirect, setRedirect] = useState<RedirectState>(
      shouldRestore ? { provider: storedProvider, nonce: 0 } : null
  );

  const nonceRef = useRef(0);
  const nextNonce = () => ++nonceRef.current;

  useEffect(() => {
    localStorage.removeItem("sessionTimeoutMs");
    localStorage.removeItem("requestStartTime");
    logger.debug("Cleared sessionTimeoutMs on login page load");
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const state = await getAuthStateFromSession();
        const token = await getToken();
        if (alive && state.isAuthenticated && !!token) {
          navigate("/identification2", { replace: true });
        }
      } catch {
        // swallow – stay on login
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate, startTimer]);

  useEffect(() => {
    const initialize = async () => {
      logger.debug("Login page loaded — cleaning session and resetting state");

      await logout(navigate);

      const [accessToken, skip, eidas] = await Promise.all([
        getToken(),
        isMyGovSkipEnabled(),
        isEidasEnabled(),
      ]);

      setToken(accessToken);
      setShowSkip(skip);
      setShowEidas(eidas);

      logger.debug(`Current Auth Method: ${currentMethod}`);
    };

    initialize();
  }, [navigate, currentMethod]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (hasOidcParams) return;

      const state = await getAuthStateFromSession();
      if (cancelled) return;

      const flag = sessionStorage.getItem(OIDC_REDIRECT_KEY);
      const startedAt = Number(sessionStorage.getItem(OIDC_REDIRECT_AT_KEY) || "0");
      const ageMs = Date.now() - startedAt;

      if (!state.isAuthenticated && flag === "1" && ageMs > 8000) {
        sessionStorage.removeItem(OIDC_REDIRECT_KEY);
        sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
        setRedirect(null);
      }
    })();

    const onVis = () => {
      if (document.visibilityState === "visible" && !hasOidcParams) {
        if (sessionStorage.getItem(OIDC_REDIRECT_KEY) === "1") {
          sessionStorage.removeItem(OIDC_REDIRECT_KEY);
          sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
          setRedirect(null);
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [hasOidcParams]);

  const startMyGov = () => {
    localStorage.setItem("suppressLogoutModal", "1");

    sessionStorage.setItem(OIDC_REDIRECT_KEY, "1");
    sessionStorage.setItem(OIDC_REDIRECT_AT_KEY, String(Date.now()));
    localStorage.setItem("loggedMethod", "MYGOV");
    setAuthMethod("MYGOV");
    setRedirect({ provider: "MYGOV", nonce: nextNonce() });

    const longWatchdog = window.setTimeout(() => {
      sessionStorage.removeItem(OIDC_REDIRECT_KEY);
      sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
      localStorage.removeItem("suppressLogoutModal");
      setRedirect(null);
    }, 60000);

    setTimeout(async () => {
      try {
        storeLoginMethod("MYGOV");
        await login("MYGOV");
      } catch (e) {
        logger.error("MyGov login failed:", e);
        sessionStorage.removeItem(OIDC_REDIRECT_KEY);
        sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
        localStorage.removeItem("suppressLogoutModal");
        setRedirect(null);
        navigate("/error");
      } finally {
        window.clearTimeout(longWatchdog);
      }
    }, 0);
  };

  const startEidas = () => {
    localStorage.setItem("suppressLogoutModal", "1");

    sessionStorage.setItem(OIDC_REDIRECT_KEY, "1");
    sessionStorage.setItem(OIDC_REDIRECT_AT_KEY, String(Date.now()));
    sessionStorage.setItem("authCompleting", "1");

    localStorage.setItem("loggedMethod", "EIDAS");
    setAuthMethod("EIDAS");
    setRedirect({ provider: "EIDAS", nonce: nextNonce() });

    const longWatchdog = window.setTimeout(() => {
      sessionStorage.removeItem(OIDC_REDIRECT_KEY);
      sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
      sessionStorage.removeItem("authCompleting");
      localStorage.removeItem("suppressLogoutModal");
      setRedirect(null);
    }, 60000);

    setTimeout(async () => {
      try {
        storeLoginMethod("EIDAS");
        await login("EIDAS");
      } catch (e) {
        logger.error("eIDAS login failed:", e);
        sessionStorage.removeItem("authCompleting");
        sessionStorage.removeItem(OIDC_REDIRECT_KEY);
        sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
        localStorage.removeItem("suppressLogoutModal");
        setRedirect(null);
        navigate("/error");
      } finally {
        window.clearTimeout(longWatchdog);
      }
    }, 0);
  };

  const startManual = () => {
    sessionStorage.removeItem(OIDC_REDIRECT_KEY);
    sessionStorage.removeItem(OIDC_REDIRECT_AT_KEY);
    setRedirect(null);

    localStorage.setItem("skipAuth", "1");
    storeLoginMethod("MANUAL");
    setAuthMethod("MANUAL");
    navigate("/information", { replace: true });
  };

  if (redirect && isOidcProvider(redirect.provider)) {
    return (
        <div id="BOX_LOGIN_PAGE">
          <main className="main-container" id="BOX_LOGIN_MAIN" aria-busy="true" role="main">
            <RedirectModal key={redirect.nonce} provider={redirect.provider} />
          </main>
        </div>
    );
  }

  return (
      <div id="BOX_LOGIN_PAGE">
        <main className="main-container" id="BOX_LOGIN_MAIN">
          <div className="login-card" id="BOX_LOGIN_CARD">
            <div className="login-header">
              <h1>{t("login.pageTitle")}</h1>
              <p>{t("login.pageSubtitle")}</p>
            </div>

            <div className="button-container" id="BOX_LOGIN_BUTTONS">
              <button id="BTN_MYGOV" className="auth-button mygov-button" onClick={startMyGov}>
                <div className="button-content">
                  <img
                      id="IMG_ICON_MYGOVID"
                      src="mygovid.svg"
                      alt={t("login.alt.mygovLogo")}
                      className="button-icon"
                  />
                  <div className="button-text">
                    <span className="button-title">{t("login.continueWithMyGovId")}</span>
                  </div>
                </div>
              </button>

              {showEidas && (
                <button id="BTN_EIDAS" className="auth-button eidas-button" onClick={startEidas}>
                  <div className="button-content">
                    <img
                      id="IMG_ICON_EIDAS"
                      src="eidas.svg"
                      alt={t("login.alt.eidasLogo")}
                      className="button-icon"
                    />
                    <div className="button-text">
                      <span className="button-title">{t("login.continueWithEidas")}</span>
                    </div>
                  </div>
                </button>
              )}

              {showSkip && (
                  <button
                      id="BTN_SKIP_AUTH"
                      className="auth-button skip-button"
                      onClick={startManual}
                  >
                    <div className="button-content">
                      <div className="button-text">
                        <span className="button-title">{t("login.skipAuthentication")}</span>
                      </div>
                    </div>
                  </button>
              )}
            </div>

            <div className="login-footer">
              <p className="security-note">
                <span className="security-icon">🔒</span>
                {t("login.securityNote")}
              </p>
            </div>

          </div>
        </main>
      </div>
  );
};

export default LoginRequester;
